import { bucketIndex, cellId, compareDecimal, createCell, decimalToNumber, multiplyDecimalInteger } from '@maya/contracts';
import { DEMO_CONFIG, getMarket } from '@maya/config';
import { TouchProbabilityModel, ewmaSigma } from './model.js';
import { EWMA_WINDOW_SAMPLES, HIST_CAL_MODEL_ID, LONG_HISTORY_MS, calibrationCovers, forecastSigma, h600Sigma, previewCalibrated } from './calibration.js';
export { TouchProbabilityModel, ewmaSigma, floorGrossHundredths, validateProbabilityTable } from './model.js';
export { sampledBandProbability } from './sampled-probability.js';
export { linearBandProbability } from './linear-probability.js';
export * from './calibration.js';
/** The deployed model's id; still used for quotes outside the calibration map and when no map is loaded. */
export const DEPLOYED_MODEL_ID = 'maya-gaussian-linear-six-v2';
export const isCalibratedInputs = (input) => typeof input.calibrationId === 'string';
/** Local immutable indicative quote registry. No provider execution or RNG. */
export class PaperQuoteBook {
    model;
    options;
    markets = new Map();
    quotes = new Map();
    byCell = new Map();
    serial = 0;
    constructor(options) { this.options = options; this.model = new TouchProbabilityModel(options.table); }
    updateMarket(marketId, samples, health, clock) {
        const previous = this.markets.get(marketId);
        const history = samples.slice(-EWMA_WINDOW_SAMPLES);
        // Earlier gaps may be filled without advancing the last timestamp or the
        // bounded history length. Every persisted point contributes to volatility.
        const recentChanged = history.length !== previous?.samples.length || history.some((sample, index) => sample.sampleId !== previous?.samples[index]?.sampleId);
        // The h600 estimator needs ten minutes of path; callers pass about two, so the book accumulates them.
        const long = previous?.long ?? new Map();
        const latestMs = history.at(-1)?.sourceTimeMs;
        let longChanged = false;
        if (latestMs !== undefined) {
            for (const time of long.keys())
                if (time < latestMs - LONG_HISTORY_MS || time > latestMs) {
                    long.delete(time);
                    longChanged = true;
                }
            for (const sample of samples) {
                if (sample.sourceTimeMs < latestMs - LONG_HISTORY_MS || sample.sourceTimeMs > latestMs)
                    continue;
                const value = decimalToNumber(sample.price);
                if (long.get(sample.sourceTimeMs) !== value) {
                    long.set(sample.sourceTimeMs, value);
                    longChanged = true;
                }
            }
        }
        const changed = recentChanged || longChanged;
        const sigma = recentChanged ? ewmaSigma(history.map((s) => ({ sourceTimeMs: s.sourceTimeMs, value: decimalToNumber(s.price) }))) : previous?.sigma ?? 0;
        const calibration = this.options.calibrations?.get(marketId);
        const sigmaH600 = !changed && previous ? previous.sigmaH600 : latestMs === undefined ? undefined : h600Sigma(long, latestMs);
        const sigmaForecast = !changed && previous ? previous.sigmaForecast : calibration && latestMs !== undefined ? forecastSigma(calibration.forecast, sigma, sigmaH600, latestMs) : 0;
        this.markets.set(marketId, { samples: history, long, health, clock, sigma, sigmaH600, sigmaForecast, revision: changed ? (previous?.revision ?? 0) + 1 : previous?.revision ?? 0, cache: changed ? new Map() : previous?.cache ?? new Map() });
        const historicalChange = changed && history.at(-1)?.sampleId === previous?.samples.at(-1)?.sampleId;
        if (historicalChange)
            for (const [id, quote] of this.byCell)
                if (quote.marketId === marketId)
                    this.byCell.delete(id);
        // Retain recent immutable IDs for pending/accepted acknowledgement, never indefinitely.
        for (const [id, quote] of this.quotes)
            if (quote.validUntilMs < clock.nowMs - 60_000)
                this.quotes.delete(id);
        for (const [id, quote] of this.byCell)
            if (quote.endMs < clock.nowMs - 5000)
                this.byCell.delete(id);
    }
    getQuote(quoteId) { return this.quotes.get(quoteId); }
    /** The 16 s EWMA sigma (the live model's input, p_live). */
    getSigma(marketId) { return this.markets.get(marketId)?.sigma ?? 0; }
    /** The HAR forecast sigma_f (input of the calibration map), 0 without a calibration or inputs. */
    getSigmaForecast(marketId) { return this.markets.get(marketId)?.sigmaForecast ?? 0; }
    getSigmaH600(marketId) { return this.markets.get(marketId)?.sigmaH600; }
    getCalibrationId(marketId) { return this.options.calibrations?.get(marketId)?.calibrationId; }
    quoteCell(cell, currentClock) {
        const unavailable = (reason) => ({ available: false, cellId: cell.cellId, reason });
        const state = this.markets.get(cell.marketId);
        if (!state)
            return unavailable('warming-up');
        const clock = currentClock ?? state.clock;
        if (!clock.ready)
            return unavailable('clock-uncertain');
        if (state.health.status !== 'ready')
            return unavailable(state.health.status === 'loading' ? 'warming-up' : state.health.status === 'incompatible' ? 'model-incompatible' : 'stale-source');
        const config = this.options.getConfig();
        const latest = state.samples.at(-1);
        if (!latest || state.samples.length < 30)
            return unavailable('insufficient-history');
        if (clock.nowMs + clock.uncertaintyMs - latest.sourceTimeMs > 7000)
            return unavailable('stale-source');
        if (state.samples.some((s, i) => i > 0 && s.sourceTimeMs - state.samples[i - 1].sourceTimeMs !== 1000))
            return unavailable('insufficient-history');
        const market = getMarket(cell.marketId);
        const bucket = bucketIndex(cell.lowerPrice, market.bandHeight);
        if (cell.cellId !== cellId(cell) || cell.endMs - cell.startMs !== 5000 || cell.startMs % 5000 !== 0 || cell.ruleVersion !== DEMO_CONFIG.ruleVersion || compareDecimal(cell.lowerPrice, multiplyDecimalInteger(market.bandHeight, bucket)) !== 0 || compareDecimal(cell.upperPrice, multiplyDecimalInteger(market.bandHeight, bucket + 1n)) !== 0)
            return unavailable('model-incompatible');
        if (config.formulaVersion !== '3.0.0')
            return unavailable('model-incompatible');
        const deadline = Math.min(latest.receivedAtMs + (this.options.quoteTtlMs ?? 1800), cell.startMs - config.minDelayMs, latest.sourceTimeMs + 7000);
        if (clock.nowMs + clock.uncertaintyMs >= deadline)
            return unavailable('too-late');
        const existing = this.byCell.get(cell.cellId);
        const reusable = existing && existing.provenance.configId === config.configId && existing.provenance.policyId === this.options.policyId && existing.ruleVersion === cell.ruleVersion && existing.validUntilMs > clock.nowMs + clock.uncertaintyMs;
        if (reusable && existing.provenance.sourceSampleId === latest.sampleId && existing.validUntilMs === deadline)
            return { available: true, quote: existing };
        const cacheKey = `${cell.cellId}:${config.configId}:${this.options.policyId}`;
        const base = {
            spot: decimalToNumber(latest.price), lower: decimalToNumber(cell.lowerPrice), upper: decimalToNumber(cell.upperPrice),
            timeToStartS: (cell.startMs - latest.sourceTimeMs) / 1000, widthS: 5,
            sigmaPerSqrtSec: state.sigma, settleDtS: 1, houseEdge: config.houseEdge, minimumGross: config.minPayoutX,
        };
        // Historic odds apply where the map has an opinion (|offset| <= 3, lead 3-35 s); elsewhere the
        // deployed model prices the cell, under the same 50x cap.
        const calibration = this.options.calibrations?.get(cell.marketId);
        const bandOffset = Number(bucket - bucketIndex(latest.price, market.bandHeight));
        const calibrated = calibration !== undefined && calibrationCovers(Math.abs(bandOffset), base.timeToStartS);
        const params = calibrated ? { ...base, sigmaForecastPerSqrtSec: state.sigmaForecast, bandOffset, calibrationId: calibration.calibrationId } : base;
        const preview = state.cache.get(cacheKey) ?? (this.options.preview ? this.options.preview(params) : isCalibratedInputs(params) ? previewCalibrated(calibration, params) : this.model.previewLinear(params));
        if (preview === undefined)
            return reusable ? { available: true, quote: existing } : unavailable('warming-up');
        state.cache.set(cacheKey, preview);
        if (!preview.available) {
            const reason = preview.reason === 'invalid-input' ? 'model-incompatible' : preview.reason === 'unreachable' ? 'out-of-domain' : preview.reason;
            return unavailable(reason);
        }
        if (calibration && BigInt(preview.grossX100) > calibration.capX100)
            return unavailable('hidden-above-cap');
        const quote = Object.freeze({
            ...cell, lowerPrice: Object.freeze({ ...cell.lowerPrice }), upperPrice: Object.freeze({ ...cell.upperPrice }),
            quoteId: `maya-quote-${crypto.randomUUID()}`, revision: ++this.serial, grossX100: preview.grossX100,
            sourceTimeMs: latest.sourceTimeMs, generatedAtMs: latest.receivedAtMs, validUntilMs: deadline,
            provenance: Object.freeze(calibrated
                ? { modelId: HIST_CAL_MODEL_ID, configId: config.configId, tableId: calibration.calibrationId, policyId: this.options.policyId, sourceSampleId: latest.sampleId }
                : { modelId: DEPLOYED_MODEL_ID, configId: config.configId, tableId: this.options.tableId, policyId: this.options.policyId, sourceSampleId: latest.sampleId }), simulated: true,
        });
        this.quotes.set(quote.quoteId, quote);
        this.byCell.set(cell.cellId, quote);
        return { available: true, quote };
    }
    candidates(marketId, clock) {
        const state = this.markets.get(marketId);
        const last = state?.samples.at(-1);
        const current = clock ?? state?.clock;
        if (!last || !current?.ready)
            return [];
        const step = getMarket(marketId).bandHeight;
        const middle = bucketIndex(last.price, step);
        const threshold = current.nowMs + current.uncertaintyMs + this.options.getConfig().minDelayMs;
        const firstStart = (Math.floor(threshold / 5000) + 1) * 5000;
        const cells = [];
        for (let column = 0; column < 6; column++)
            for (let offset = -3; offset <= 3; offset++) {
                const index = middle + BigInt(offset);
                cells.push(createCell(marketId, firstStart + column * 5000, multiplyDecimalInteger(step, index), multiplyDecimalInteger(step, index + 1n), DEMO_CONFIG.ruleVersion));
            }
        return cells;
    }
}
//# sourceMappingURL=index.js.map